import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Tumbling reels — TWO separate clusters win on the SAME reveal (an L4 cluster top-right and an
 * L2 cluster bottom-left). Both are highlighted, then ALL their cells are removed together by one
 * mask, and the single collapse drops in with no further win. Exercises multi-cluster removal in
 * one tumble step. Cluster/mask wins, no payline.
 */
export function baseFixtureReelTumbleDouble(): SpinResponse {
  const win1 = mask([3, 4, 8, 9]);          // L4 ×4 (top-right)
  const win2 = mask([10, 11, 15, 16]);      // L2 ×4 (bottom-left)
  const removeAll = mask([3, 4, 8, 9, 10, 11, 15, 16]); // both clusters

  return spinFixture("reel-tumble-double")
    .balance("EUR", "100000")
    // --- seq 0: reveal — two clusters win, all removed together ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("400").gridRows([
        ["L3", "L5", "L2", "L4", "L4"],
        ["L5", "L3", "L5", "L4", "L4"],
        ["L2", "L2", "L4", "L5", "L3"],
        ["L2", "L2", "L1", "L5", "L3"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const wa = fx.gridWinHighlight({
        positionMask: win1, winAmount: "200", symbolIdentifier: "L4", symbolCount: "4", // 100 × 2
        lines: [{ positionMask: win1, winAmount: "200", baseWinAmount: "100", multiplierApplied: "2" }],
      });
      e.action(wa.type, wa.payload, { actionPhaseName: wa.phase, isSkippable: true, debugTag: "winL4" });

      const wb = fx.gridWinHighlight({
        positionMask: win2, winAmount: "200", symbolIdentifier: "L2", symbolCount: "4", // 100 × 2
        lines: [{ positionMask: win2, winAmount: "200", baseWinAmount: "100", multiplierApplied: "2" }],
      });
      e.action(wb.type, wb.payload, { actionPhaseName: wb.phase, isSkippable: true, debugTag: "winL2" });

      // ONE fire for the substep — the same multiplier (x2) applies to both highlights.
      const fire = cannonFire(0);
      e.action(fire.type, fire.payload, { actionPhaseName: fire.phase, debugTag: "cannon_fire" });

      const r = fx.removeSymbolsByMask(removeAll);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove" });

      const t = fx.updateTotalWinMeter("400");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total" });
    })
    // --- seq 1: collapse — no further win, round ends ---
    .event("collapse", 1, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("400").gridRows([
        ["L1", "L4", "L2", "L2", "L3"],
        ["L5", "L3", "L5", "L1", "L2"],
        ["L3", "L5", "L4", "L5", "L3"],
        ["L5", "L3", "L1", "L5", "L3"],
      ]);

      const end = fx.spinBoundaryEnd("base:1", "400");
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
